import { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Menu, User, CreditCard, LogOut, Loader2, AlertTriangle, Eye, EyeOff, Lock, Users, Wrench, Clock, ArrowRight, ArrowDown, Shield, CheckCircle2, Sparkles, Zap, Star, Trophy, ChevronDown, ChevronUp, Megaphone, Video, PenTool, Compass, Target, Rocket, Globe, Plus } from 'lucide-react';
import PlanSelectionDialog from '@/components/PlanSelectionDialog';
import FreeTrialPopup from '@/components/FreeTrialPopup';
import LightBackground from '@/components/LightBackground';
import LightLogo from '@/components/LightLogo';

import Footer from '@/components/Footer';
import { academiesData } from '@/data/academyData';
import { useAuth } from '@/hooks/useAuth';
import { useModuleProgress } from '@/hooks/useModuleProgress';
import { useSubscription } from '@/hooks/useSubscription';
import { useClientAccess } from '@/hooks/useClientAccess';
import { useAICredits } from '@/hooks/useAICredits';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { LanguageSelector } from '@/components/LanguageSelector';
import { TranslatedText } from '@/components/TranslatedText';
import { useAdminSettings } from '@/hooks/useAdminSettings';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LaunchKitDashboardCard } from '@/components/LaunchKitDashboardCard';
import auraMascot from '@/assets/aura-mascot.png';


const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, role, loading: authLoading, signOut } = useAuth();
  const { academyProgress, completedModules, isLoading: progressLoading, getAcademyProgress } = useModuleProgress();
  const { createCheckoutSession, openCustomerPortal, loading: subscriptionLoading } = useSubscription();
  const { loading: clientAccessLoading, clientInfo, showSubscriptionWall: shouldShowSubscriptionWall, showUpgradeBanner } = useClientAccess();
  const { monthlyPrice, lifetimePrice } = useAdminSettings();
  const { balance: aiCreditsBalance, loading: creditsLoading } = useAICredits();
  const [memberName, setMemberName] = useState<string>('');
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
  const [bypassSubscriptionWall, setBypassSubscriptionWall] = useState(false);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [showAllAcademies, setShowAllAcademies] = useState(false);
  
  // Password change modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  
  // Show subscription wall unless user chose to proceed as freemium
  const showSubscriptionWall = shouldShowSubscriptionWall && !bypassSubscriptionWall;
  
  // Check if user needs to set password (from email link)
  useEffect(() => {
    const requirePassword = searchParams.get('require_password');
    if (requirePassword === 'true' && user) {
      setShowPasswordModal(true);
      // Clear the URL param
      searchParams.delete('require_password');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, user, setSearchParams]);
  
  const handlePasswordUpdate = async () => {
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setIsUpdatingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      
      toast.success('Password created successfully!');
      setShowPasswordModal(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      toast.error(error.message || 'Failed to update password');
    } finally {
      setIsUpdatingPassword(false);
    }
  };




  // Remove custom cursor on dashboard (light theme uses normal cursor)
  useEffect(() => {
    document.body.style.cursor = 'auto';
  }, []);

  // Require authentication
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [authLoading, user, navigate]);

  // Admins should never land on the members dashboard
  useEffect(() => {
    if (!authLoading && user && role === 'admin') {
      navigate('/admin', { replace: true });
    }
  }, [authLoading, user, role, navigate]);

  // Fetch member name from client record or sessionStorage fallback
  useEffect(() => {
    const fetchMemberName = async () => {
      if (!user) return;
      
      // First try to get from client record
      const { data } = await supabase
        .from('clients')
        .select('name')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (data) {
        const firstName = data.name?.split(' ')[0];
        setMemberName(firstName || '');
        return;
      }
      
      // Fallback: check sessionStorage for freemium users who just signed up
      const freemiumIntent = sessionStorage.getItem('freemiumIntent');
      if (freemiumIntent) {
        try {
          const { name } = JSON.parse(freemiumIntent);
          const firstName = name?.split(' ')[0];
          setMemberName(firstName || '');
        } catch (e) {
          // Ignore parse errors
        }
      }
    };
    
    fetchMemberName();
  }, [user]);

  // Derive values from clientInfo
  const isLifetimeMember = clientInfo.isLifetimeAccess;
  const daysRemaining = clientInfo.daysRemaining;
  const isTestAccount = clientInfo.isTestAccount;

  // Fetch academy statuses from database
  const [academyStatuses, setAcademyStatuses] = useState<Record<string, string>>({});
  
  useEffect(() => {
    const fetchAcademyStatuses = async () => {
      const { data } = await supabase
        .from('academies')
        .select('id, status');
      
      if (data) {
        const statusMap: Record<string, string> = {};
        data.forEach((academy) => {
          statusMap[academy.id] = academy.status;
        });
        setAcademyStatuses(statusMap);
      }
    };
    
    fetchAcademyStatuses();
  }, []);

  // Calculate real progress for each academy
  const academies = useMemo(() => {
    return academiesData.map((academy, index) => {
      const progress = getAcademyProgress(academy.id);
      const progressPercent = progress.total > 0 
        ? Math.round((progress.completed / progress.total) * 100) 
        : 0;
      
      return {
        id: academy.id,
        name: academy.name,
        description: academy.description,
        modules: academy.modules.length,
        progress: progressPercent,
        completedModules: progress.completed,
        totalModules: progress.total,
        unlocked: true, // Full access - 5K plan
        status: academy.comingSoon ? 'coming_soon' : (academyStatuses[academy.id] || 'active'),
      };
    });
  }, [academyProgress, getAcademyProgress, academyStatuses]);

  // Track class completions for stats
  const [classCompletions, setClassCompletions] = useState<number>(0);
  
  // Total classes for the currently launched academy (1 academy × 10 modules × 10 classes)
  const TOTAL_CLASSES = 100;

  // Fetch class completions count
  useEffect(() => {
    const fetchClassStats = async () => {
      if (!user) return;
      
      // Get client ID first
      const { data: clientData } = await supabase
        .from('clients')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (!clientData) return;
      
      // Get count of passed class completions
      const { count: completedCount } = await supabase
        .from('class_completions')
        .select('*', { count: 'exact', head: true })
        .eq('client_id', clientData.id)
        .eq('passed', true);
      
      setClassCompletions(completedCount || 0);
    };
    
    fetchClassStats();
  }, [user]);

  // Calculate overall stats
  const stats = useMemo(() => {
    const academiesCompleted = academies.filter(a => a.progress === 100).length;
    const totalAcademies = academies.length;
    const classPercentage = Math.round((classCompletions / TOTAL_CLASSES) * 100);
    
    return {
      academiesCompleted: `${academiesCompleted}/${totalAcademies}`,
      classesCompleted: classCompletions.toString(),
      classPercentage: `${classPercentage}%`,
    };
  }, [academies, classCompletions]);

  // Show loading while checking auth, subscription, or progress
  if (authLoading || clientAccessLoading || progressLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Subscription Wall
  if (showSubscriptionWall) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-background relative overflow-hidden">
        <LightBackground variant="default" />
        
        <div className="absolute inset-0 opacity-40 pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 md:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="text-center w-full max-w-5xl"
          >
            <div className="mb-8 flex justify-center">
              <LightLogo size="lg" />
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              <TranslatedText>Complete Your Membership</TranslatedText>
            </h1>

            <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
              <TranslatedText>Welcome back! To access the FA Club dashboard and all member benefits, please choose your subscription plan.</TranslatedText>
            </p>

            {/* Plan Options */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-8 w-full">
              {/* Monthly Plan */}
              <motion.div
                className="relative p-6 sm:p-8 lg:p-10 bg-card rounded-2xl shadow-lg border border-border flex flex-col"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <div className="relative z-10 flex flex-col h-full">
                  {/* Plan title */}
                  <div className="text-center mb-6">
                    <p className="text-[10px] sm:text-xs tracking-[0.2em] sm:tracking-[0.3em] uppercase text-green-600/80 mb-1 sm:mb-2">
                      Most Popular
                    </p>
                    <h3 className="text-xl sm:text-2xl font-bold text-foreground">Monthly</h3>
                  </div>

                  {/* Price display */}
                  <div className="text-center mb-6">
                    <div className="mb-1">
                      <span className="text-base sm:text-lg text-muted-foreground line-through">$49</span>
                    </div>
                    <div className="flex items-baseline justify-center gap-1 sm:gap-2">
                      <span className="text-4xl sm:text-5xl font-bold text-foreground">
                        ${monthlyPrice}
                      </span>
                      <span className="text-sm sm:text-lg text-muted-foreground font-light">/mo</span>
                    </div>
                    <p className="text-[10px] sm:text-xs text-green-600 font-semibold mt-2 px-2">Cancel anytime</p>
                  </div>

                  {/* Divider */}
                  <div className="h-px w-full bg-gradient-to-r from-transparent via-border to-transparent mb-6" />

                  {/* Features list */}
                  <div className="space-y-2 sm:space-y-3 mb-6 flex-1">
                    {[
                      "Modules unlock as you complete each one",
                      "Automated templates and systems",
                      "Secret knowledge library",
                      "Networking with the faceless elite",
                      "Monthly access to content",
                      "Priority 24/7 support",
                    ].map((feature, index) => (
                      <div key={index} className="flex items-start gap-2 sm:gap-3">
                        <div className="w-3 sm:w-4 h-3 sm:h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                          <div className="w-2 h-2 rounded-full bg-blue-500" />
                        </div>
                        <span className="text-xs sm:text-sm text-muted-foreground">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <Button
                    size="lg"
                    className="w-full bg-foreground hover:bg-foreground/90 text-background font-semibold h-14 rounded-xl"
                    disabled={isProcessingCheckout}
                    onClick={async () => {
                      try {
                        setIsProcessingCheckout(true);
                        await createCheckoutSession('monthly');
                      } catch (error) {
                        console.error('Checkout error:', error);
                        toast.error('Failed to start checkout. Please try again.');
                      } finally {
                        setIsProcessingCheckout(false);
                      }
                    }}
                  >
                    {isProcessingCheckout ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Get Monthly Access'
                    )}
                  </Button>
                </div>
              </motion.div>

              {/* Lifetime Plan - Featured */}
              <motion.div
                className="relative p-6 sm:p-8 lg:p-10 bg-card rounded-2xl shadow-xl border-2 border-primary/30 flex flex-col"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                {/* Best Value Badge */}
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="px-4 py-1 bg-blue-600 text-white text-xs font-bold uppercase rounded-full shadow-lg">
                    Best Value
                  </span>
                </div>

                <div className="relative z-10 flex flex-col h-full">
                  {/* Plan title */}
                  <div className="text-center mb-6">
                    <p className="text-[10px] sm:text-xs tracking-[0.2em] sm:tracking-[0.3em] uppercase text-blue-600/60 mb-1 sm:mb-2">
                      One-Time Payment
                    </p>
                    <h3 className="text-xl sm:text-2xl font-bold text-foreground">Lifetime Access</h3>
                  </div>

                  {/* Price display */}
                  <div className="text-center mb-6">
                    <div className="mb-1">
                      <span className="text-base sm:text-lg text-muted-foreground line-through">$197</span>
                    </div>
                    <div className="flex items-baseline justify-center gap-1 sm:gap-2">
                      <span className="text-4xl sm:text-5xl font-bold text-blue-600">
                        ${lifetimePrice}
                      </span>
                    </div>
                    <p className="text-[10px] sm:text-xs text-blue-600/60 mt-2 px-2">🚀 Access Everything from day one • No monthly fees ever</p>
                  </div>

                  {/* Divider */}
                  <div className="h-px w-full bg-gradient-to-r from-transparent via-primary/20 to-transparent mb-6" />

                  {/* Features list */}
                  <div className="space-y-3 mb-6 flex-1">
                    {[
                      "Faceless Content System unlocked instantly",
                      "10 implementation modules available on spot",
                      "500+ short lessons accessible immediately",
                      "Hands-on exercises after every lesson",
                      "Automated templates and systems",
                      "Networking with the faceless elite",
                      "Priority 24/7 support",
                    ].map((feature, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <div className="w-2 h-2 rounded-full bg-green-500" />
                        </div>
                        <span className="text-sm text-muted-foreground">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Button
                    size="lg"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold h-14 rounded-xl shadow-lg shadow-blue-600/25"
                    disabled={isProcessingCheckout}
                    onClick={async () => {
                      try {
                        setIsProcessingCheckout(true);
                        await createCheckoutSession('lifetime');
                      } catch (error) {
                        console.error('Checkout error:', error);
                        toast.error('Failed to start checkout. Please try again.');
                      } finally {
                        setIsProcessingCheckout(false);
                      }
                    }}
                  >
                    {isProcessingCheckout ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Get Lifetime Access'
                    )}
                  </Button>
                </div>
              </motion.div>
            </div>

            {/* Trust elements */}
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mb-6">
              <Shield className="w-4 h-4 text-green-500" />
              <span>Secure payment • 7-day guarantee</span>
            </div>

            {/* Freemium Option */}
            <div
              onClick={async () => {
                if (user) {
                  try {
                    const { data: existingClient } = await supabase
                      .from('clients')
                      .select('id')
                      .eq('user_id', user.id)
                      .maybeSingle();
                    
                    if (!existingClient) {
                      const { error } = await supabase.rpc('create_freemium_client' as any, {
                        p_user_id: user.id,
                        p_email: user.email || '',
                        p_name: user.user_metadata?.name || user.email?.split('@')[0] || 'Member'
                      });
                      
                      if (error) {
                        console.error('Error creating freemium client:', error);
                        toast.error('Failed to set up freemium access');
                        return;
                      }
                    }
                  } catch (err) {
                    console.error('Freemium setup error:', err);
                  }
                }
                setBypassSubscriptionWall(true);
                toast.success('Welcome! You have access to Academy 1, Module 1, Class 1.');
              }}
              className="cursor-pointer rounded-2xl border-2 border-primary/30 bg-gradient-to-br from-primary/10 to-accent/10 p-5 mb-6 hover:border-primary/60 hover:shadow-lg transition-all duration-300 active:scale-[0.98]"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-bold text-primary mb-1">🎓 Watch 1 FREE Class</p>
                  <p className="text-sm text-muted-foreground">No credit card required • Instant access</p>
                </div>
                <div className="text-blue-500">
                  <ArrowRight className="w-6 h-6" />
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs text-blue-600 font-medium">
                <CheckCircle2 className="w-4 h-4" />
                <span>Preview Academy 1, Module 1, Class 1 for free</span>
              </div>
            </div>

            <p className="text-muted-foreground text-sm mb-4">
              Need help? Contact us at{' '}
              <a href="mailto:info@facelessaura.club" className="text-blue-600 hover:underline">
                info@facelessaura.club
              </a>
            </p>

            <Button
              variant="ghost"
              size="sm"
              onClick={signOut}
              className="text-slate-500 hover:text-blue-600"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <LightBackground variant="subtle" />
      
      {/* Free Trial Popup for freemium users */}
      <FreeTrialPopup isFreemium={clientInfo.clientType === 'freemium'} delay={15} />
      
      {/* Password Change Modal - keeping existing logic but updating styles */}
      <AnimatePresence>
        {showPasswordModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative w-full max-w-md p-8 bg-white rounded-2xl shadow-2xl"
            >
              <div className="text-center mb-6">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
                  <Lock className="w-6 h-6 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900"><TranslatedText>Create Your Password</TranslatedText></h2>
                <p className="text-sm text-slate-600 mt-2">
                  <TranslatedText>Welcome to FA Club! Please create a secure password for your account.</TranslatedText>
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="relative">
                  <Input
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="New password (min 6 characters)"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="bg-slate-50 border-slate-200 focus:border-blue-500 pr-10 h-12 rounded-xl"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                
                <div className="relative">
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="bg-slate-50 border-slate-200 focus:border-blue-500 pr-10 h-12 rounded-xl"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                
                <Button
                  size="lg"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold h-14 rounded-xl"
                  onClick={handlePasswordUpdate}
                  disabled={isUpdatingPassword || !newPassword || !confirmPassword}
                >
                  {isUpdatingPassword ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Password'
                  )}
                </Button>
                
                <button
                  onClick={() => setShowPasswordModal(false)}
                  className="w-full text-sm text-slate-500 hover:text-blue-600 transition-colors"
                >
                  Skip for now
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Freemium Account Upgrade Banner (freemium only) */}
      {clientInfo.clientType === 'freemium' && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-30 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-blue-200 px-6 py-3"
        >
          <div className="flex items-center justify-center gap-3 text-sm flex-wrap">
            <span className="text-foreground font-semibold">
              🚀 Unlock the Full Faceless Content System — Start building today!
            </span>
            <button
              onClick={async () => {
                setIsProcessingCheckout(true);
                try { await createCheckoutSession('monthly'); } catch { toast.error('Failed to start checkout'); } finally { setIsProcessingCheckout(false); }
              }}
              disabled={isProcessingCheckout}
              className="ml-2 px-4 py-1.5 text-xs font-semibold uppercase bg-green-600 hover:bg-green-700 text-white rounded-full transition-colors animate-pulse"
            >
              Upgrade Now
            </button>
          </div>
        </motion.div>
      )}

      {/* Payment Failed / Grace Period Banner */}
      {clientInfo.isGracePeriod && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-30 bg-gradient-to-r from-red-50 to-orange-50 border-b border-red-200 px-6 py-4"
        >
          <div className="flex items-center justify-center gap-3 text-sm flex-wrap">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <span className="text-red-700 font-semibold">
              We couldn't process your payment. Your access will expire in {clientInfo.daysRemaining !== null && clientInfo.daysRemaining >= 0 ? `${clientInfo.daysRemaining} day${clientInfo.daysRemaining === 1 ? '' : 's'}` : 'soon'}.
            </span>
            <button
              onClick={async () => {
                setIsProcessingCheckout(true);
                try { await createCheckoutSession('monthly'); } catch { toast.error('Failed to start checkout'); } finally { setIsProcessingCheckout(false); }
              }}
              disabled={isProcessingCheckout}
              className="ml-2 px-5 py-2 text-xs font-bold uppercase bg-red-600 hover:bg-red-700 text-white rounded-full transition-colors"
            >
              {isProcessingCheckout ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Pay Now →'}
            </button>
          </div>
        </motion.div>
      )}

      {/* Trial Info Banner */}
      {clientInfo.isTrialing && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-30 bg-gradient-to-r from-cyan-50 to-blue-50 border-b border-cyan-200 px-6 py-3"
        >
          <div className="flex items-center justify-center gap-3 text-sm flex-wrap">
            <Clock className="w-4 h-4 text-cyan-600" />
            <span className="text-cyan-700 font-medium">
              🎉 Free trial active — {clientInfo.daysRemaining !== null && clientInfo.daysRemaining >= 0 ? `${clientInfo.daysRemaining} day${clientInfo.daysRemaining === 1 ? '' : 's'} remaining` : 'ending soon'}. Enjoy full access!
            </span>
          </div>
        </motion.div>
      )}

      {/* Header */}
      <motion.header
        className="relative z-20 p-6 flex justify-between items-center border-b border-border"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Link to="/" className="flex items-center">
          <LightLogo size="md" />
        </Link>
        <div className="flex items-center gap-2 sm:gap-4">
          <ThemeToggle className="text-muted-foreground hover:text-foreground" />
          <LanguageSelector variant="ghost" className="text-muted-foreground hover:text-foreground" />
          {isTestAccount && (
            <span className="px-3 py-1 text-xs font-medium uppercase bg-accent/10 border border-accent/20 text-accent rounded-full">
              🧪 Test {clientInfo.clientType === 'test_lifetime' ? 'Lifetime' : 'Monthly'}
            </span>
          )}
          {!isTestAccount && isLifetimeMember ? (
            <span className="px-3 py-1 text-xs font-medium uppercase bg-primary/10 border border-primary/20 text-primary rounded-full">
              ✦ Lifetime
            </span>
          ) : !isTestAccount && clientInfo.clientType === 'monthly' && daysRemaining !== null && (
            <span className="px-3 py-1 text-xs bg-muted border border-border text-muted-foreground rounded-full">
              {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining
            </span>
          )}
          <span className="text-sm text-muted-foreground"><TranslatedText>Welcome</TranslatedText>, {memberName || 'Member'}</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-primary hover:bg-primary/10">
                <Menu className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-card border-border">
              <DropdownMenuItem 
                className="text-foreground hover:bg-primary/10 hover:text-primary cursor-pointer"
                onClick={() => navigate('/profile')}
              >
                <User className="mr-2 h-4 w-4" />
                <TranslatedText>Profile</TranslatedText>
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="text-foreground hover:bg-primary/10 hover:text-primary cursor-pointer"
                onClick={() => navigate('/affiliates')}
              >
                <Users className="mr-2 h-4 w-4" />
                <TranslatedText>Affiliates Program</TranslatedText>
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="text-foreground hover:bg-primary/10 hover:text-primary cursor-pointer"
                onClick={async () => {
                  if (clientInfo.clientType === 'freemium') {
                    setBypassSubscriptionWall(false);
                    return;
                  }
                  try {
                    await openCustomerPortal();
                  } catch (error: any) {
                    console.error('Portal error:', error);
                    if (error?.message?.includes('No Stripe customer found')) {
                      toast.error('Subscription management is not available for your account type. Please contact support.');
                    } else {
                      toast.error('Failed to open billing portal. Please try again.');
                    }
                  }
                }}
              >
                <CreditCard className="mr-2 h-4 w-4" />
                <TranslatedText>Manage Subscription</TranslatedText>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-border" />
              <DropdownMenuItem 
                className="text-foreground hover:bg-primary/10 hover:text-primary cursor-pointer"
                onClick={async () => { await signOut(); }}
              >
                <LogOut className="mr-2 h-4 w-4" />
                <TranslatedText>Sign Out</TranslatedText>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </motion.header>

      {/* Main content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Welcome + Stats Header */}
        <motion.div
          className="mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
            <div>
              <p className="text-sm text-muted-foreground mb-1"><TranslatedText>Welcome back</TranslatedText>, {memberName || 'Member'}</p>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                <TranslatedText>Members Dashboard</TranslatedText>
              </h1>
            </div>
            {/* Compact stats */}
            <div className="flex items-center gap-3 flex-wrap">
              {[
                { label: 'Academies', value: stats.academiesCompleted },
                { label: 'Classes', value: stats.classesCompleted },
                { label: 'Progress', value: stats.classPercentage },
              ].map((stat, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 bg-card rounded-xl border border-border">
                  <span className="text-sm font-bold text-primary">{stat.value}</span>
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider"><TranslatedText>{stat.label}</TranslatedText></span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* ═══ AI TEACHER SPOTLIGHT ═══ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="mb-10"
        >
          <div 
            className="relative overflow-hidden rounded-2xl border border-primary/20 cursor-pointer group"
            onClick={() => window.dispatchEvent(new CustomEvent('open-study-assistant'))}
          >
            {/* Animated gradient background */}
            <motion.div 
              className="absolute inset-0 bg-gradient-to-br from-primary/8 via-background to-accent/8"
              animate={{ 
                background: [
                  'linear-gradient(135deg, hsl(var(--primary) / 0.08) 0%, hsl(var(--background)) 50%, hsl(var(--accent) / 0.08) 100%)',
                  'linear-gradient(135deg, hsl(var(--accent) / 0.08) 0%, hsl(var(--background)) 50%, hsl(var(--primary) / 0.08) 100%)',
                  'linear-gradient(135deg, hsl(var(--primary) / 0.08) 0%, hsl(var(--background)) 50%, hsl(var(--accent) / 0.08) 100%)',
                ]
              }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            />

            {/* Floating orbs */}
            <motion.div 
              className="absolute -top-10 -right-10 w-40 h-40 bg-primary/10 rounded-full blur-3xl"
              animate={{ scale: [1, 1.3, 1], x: [0, 15, 0], y: [0, -10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div 
              className="absolute -bottom-10 left-1/4 w-32 h-32 bg-accent/10 rounded-full blur-3xl"
              animate={{ scale: [1, 1.2, 1], x: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            />

            <div className="relative z-10 p-5 sm:p-6">
              <div className="flex items-start gap-5">
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <motion.span 
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold bg-green-500/15 text-green-600 rounded-full border border-green-500/20"
                      animate={{ opacity: [1, 0.7, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                      Live Now
                    </motion.span>
                    <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold bg-primary/10 text-primary rounded-full">
                      <TranslatedText>AI Personal Teacher</TranslatedText>
                    </span>
                  </div>

                  <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-1.5 group-hover:text-primary transition-colors">
                    Aura AI
                  </h3>
                  
                  {/* Simulated chat bubble — typing effect */}
                  <motion.div 
                    className="inline-flex items-start gap-2 bg-muted/60 rounded-2xl rounded-tl-md px-4 py-2.5 mb-3 max-w-sm"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.8 }}
                  >
                    <p className="text-sm text-foreground/80 leading-relaxed">
                      <TranslatedText>Hey! I'm your personal AI teacher. Ask me anything about the course — I'll guide you step by step 🚀</TranslatedText>
                    </p>
                  </motion.div>

                  <div className="flex items-center gap-3 flex-wrap">
                    <motion.div
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <Button
                        className="bg-foreground text-background hover:bg-foreground/90 rounded-xl h-10 sm:h-11 px-5 sm:px-6 font-semibold gap-2 text-sm shadow-lg shadow-foreground/10"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.dispatchEvent(new CustomEvent('open-study-assistant'));
                        }}
                      >
                        <Sparkles className="w-4 h-4" />
                        <TranslatedText>Talk to Aura</TranslatedText>
                      </Button>
                    </motion.div>
                    <span className="text-[11px] text-muted-foreground">
                      ✦ <TranslatedText>Free & unlimited for members</TranslatedText>
                    </span>
                  </div>
                </div>

                {/* Mascot peeking from behind the card */}
                <motion.div
                  className="relative flex-shrink-0 hidden sm:block"
                  initial={{ y: 40, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.7, delay: 0.4, type: "spring", stiffness: 120 }}
                >
                  <div className="relative -mr-3 -mb-6">
                    {/* Glow behind mascot */}
                    <motion.div
                      className="absolute inset-0 rounded-full bg-gradient-to-t from-primary/25 to-accent/25 blur-xl scale-110"
                      animate={{ scale: [1.1, 1.25, 1.1], opacity: [0.4, 0.7, 0.4] }}
                      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    />
                    {/* Mascot */}
                    <motion.img 
                      src={auraMascot} 
                      alt="Aura AI" 
                      className="w-28 h-28 lg:w-32 lg:h-32 rounded-full object-cover border-3 border-primary/25 relative z-10 shadow-2xl"
                      animate={{ y: [0, -8, 0] }}
                      transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
                    />
                    {/* Sparkle indicator */}
                    <motion.div
                      className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-background z-20 flex items-center justify-center shadow-lg shadow-green-500/30"
                      animate={{ scale: [1, 1.25, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <Sparkles className="w-3 h-3 text-white" />
                    </motion.div>
                    {/* Floating particles around mascot */}
                    {[0, 1, 2].map((i) => (
                      <motion.div
                        key={i}
                        className="absolute w-1.5 h-1.5 rounded-full bg-primary/40"
                        style={{
                          top: `${20 + i * 30}%`,
                          left: i % 2 === 0 ? '-8px' : 'auto',
                          right: i % 2 !== 0 ? '-8px' : 'auto',
                        }}
                        animate={{ 
                          y: [0, -12, 0], 
                          opacity: [0.3, 0.8, 0.3],
                          scale: [0.8, 1.2, 0.8],
                        }}
                        transition={{ duration: 2 + i * 0.5, repeat: Infinity, delay: i * 0.4 }}
                      />
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Bottom interaction hint — subtle pulse */}
            <motion.div 
              className="relative z-10 px-5 sm:px-6 pb-4 -mt-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
            >
              <div className="flex items-center gap-2 text-[10px] text-muted-foreground/60">
                <motion.span 
                  className="inline-block w-4 h-[2px] bg-primary/30 rounded-full"
                  animate={{ width: ['16px', '24px', '16px'] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
                <TranslatedText>Click anywhere to start chatting</TranslatedText>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* ═══ SECTION: LEARNING (Collapsed by default) ═══ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-12"
        >
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Star className="w-4 h-4 text-primary" />
              </div>
              <h2 className="text-xl font-bold text-foreground"><TranslatedText>Learning</TranslatedText></h2>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>{completedModules.length} <TranslatedText>modules completed</TranslatedText></span>
            </div>
          </div>

          {/* Primary Academy Card (always visible) */}
          {academies.length > 0 && (() => {
            const academy = academies[0];
            return (
              <motion.div
                className="relative p-5 sm:p-6 rounded-2xl bg-card border border-primary/30 shadow-lg ring-1 ring-primary/10 z-10"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.35 }}
                whileHover={{ y: -2, boxShadow: '0 8px 30px -12px hsl(220 90% 50% / 0.15)' }}
              >
                <div className="absolute -top-2.5 left-5 px-3 py-0.5 bg-primary text-primary-foreground text-[10px] uppercase tracking-wider font-semibold rounded-full shadow-sm">
                  <TranslatedText>Start Here</TranslatedText>
                </div>

                {academy.status === 'maintenance' && (
                  <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-600 dark:text-amber-400">
                    <Wrench className="w-3 h-3" />
                    <span className="text-[10px] uppercase font-medium"><TranslatedText>Maintenance</TranslatedText></span>
                  </div>
                )}

                <div className="flex items-center gap-4 sm:gap-6">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-sm bg-primary text-primary-foreground shadow-md">
                    1
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <div>
                        <span className="text-[10px] text-muted-foreground uppercase tracking-widest font-medium"><TranslatedText>Academy</TranslatedText> 1</span>
                        <h3 className="text-sm sm:text-base font-semibold text-foreground">
                          <TranslatedText cacheKey={`academy_name_${academy.id}`}>{academy.name}</TranslatedText>
                        </h3>
                      </div>
                      <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-lg hidden sm:block">{academy.modules} <TranslatedText>modules</TranslatedText></span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                        <motion.div
                          className="h-full bg-gradient-to-r from-primary to-accent rounded-full"
                          initial={{ width: 0 }}
                          animate={{ width: `${academy.progress}%` }}
                          transition={{ duration: 1, delay: 0.6 }}
                        />
                      </div>
                      <span className="text-[10px] text-muted-foreground w-8 text-right">{academy.progress}%</span>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    className="flex-shrink-0 text-xs h-9 px-4 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm"
                    onClick={() => navigate(`/academy/${academy.id}`)}
                  >
                    <TranslatedText>Continue</TranslatedText> <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </motion.div>
            );
          })()}

          {/* Stacked preview (collapsed) — shows peek of other academies */}
          {!showAllAcademies && academies.length > 1 && (
            <div className="relative mt-[-6px] z-0">
              {/* Stacked card shadows behind */}
              <div className="mx-3 h-2 rounded-b-xl bg-muted/60 border-x border-b border-border" />
              <div className="mx-6 h-2 rounded-b-xl bg-muted/30 border-x border-b border-border" />
            </div>
          )}

          {/* Expand/collapse toggle */}
          <div className="flex justify-center mt-3">
            <button
              onClick={() => setShowAllAcademies(!showAllAcademies)}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-muted-foreground hover:text-primary transition-colors rounded-full hover:bg-primary/5"
            >
              {showAllAcademies ? (
                <>
                  <ChevronUp className="w-3.5 h-3.5" />
                  <TranslatedText>Show Less</TranslatedText>
                </>
              ) : (
                <>
                  <ChevronDown className="w-3.5 h-3.5" />
                  <TranslatedText>See All</TranslatedText> {academies.length} <TranslatedText>Academies</TranslatedText>
                </>
              )}
            </button>
          </div>

          {/* Expanded academies list */}
          <AnimatePresence>
            {showAllAcademies && academies.length > 1 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.35, ease: 'easeInOut' }}
                className="overflow-hidden mt-2 space-y-2"
              >
                {academies.slice(1).map((academy, i) => {
                  const index = i + 1;
                  return (
                    <motion.div
                      key={index}
                      className="relative p-4 sm:p-5 rounded-2xl bg-card border border-border hover:border-primary/20 transition-all duration-300"
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: i * 0.06 }}
                    >
                      {academy.status === 'maintenance' && (
                        <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-600 dark:text-amber-400">
                          <Wrench className="w-3 h-3" />
                          <span className="text-[10px] uppercase font-medium"><TranslatedText>Maintenance</TranslatedText></span>
                        </div>
                      )}
                      {academy.status === 'coming_soon' && (
                        <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 bg-primary/10 border border-primary/20 rounded-lg text-primary">
                          <Clock className="w-3 h-3" />
                          <span className="text-[10px] uppercase font-medium"><TranslatedText>Coming Soon</TranslatedText></span>
                        </div>
                      )}

                      <div className="flex items-center gap-4 sm:gap-5">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-sm ${
                          academy.progress > 0 ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                        }`}>
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <div>
                              <span className="text-[10px] text-muted-foreground uppercase tracking-widest font-medium"><TranslatedText>Academy</TranslatedText> {index + 1}</span>
                              <h3 className="text-sm font-semibold text-foreground/80">
                                <TranslatedText cacheKey={`academy_name_${academy.id}`}>{academy.name}</TranslatedText>
                              </h3>
                            </div>
                            <span className="text-[10px] text-muted-foreground bg-muted px-2 py-1 rounded-lg hidden sm:block">{academy.modules} <TranslatedText>modules</TranslatedText></span>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden">
                              <motion.div
                                className="h-full bg-gradient-to-r from-primary to-accent rounded-full"
                                initial={{ width: 0 }}
                                animate={{ width: `${academy.progress}%` }}
                                transition={{ duration: 1, delay: 0.3 + i * 0.1 }}
                              />
                            </div>
                            <span className="text-[10px] text-muted-foreground w-8 text-right">{academy.progress}%</span>
                          </div>
                        </div>
                        <Button 
                          variant="ghost"
                          size="sm" 
                          className="flex-shrink-0 text-xs h-8 px-3 rounded-xl text-primary hover:bg-primary/10"
                          onClick={() => navigate(`/academy/${academy.id}`)}
                        >
                          <TranslatedText>Continue</TranslatedText> <ArrowRight className="w-3 h-3 ml-1" />
                        </Button>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* ═══ SECTION: AI BUSINESS LAUNCH KIT ═══ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45 }}
          className="mb-12"
        >
          <LaunchKitDashboardCard />
        </motion.div>

        {/* ═══ SECTION: AI TOOLS ═══ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mb-12"
        >
          {/* Header */}
          <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-primary/15 to-accent/15 flex items-center justify-center">
                  <Zap className="w-3.5 h-3.5 text-primary" />
                </div>
                <h2 className="text-xl font-bold text-foreground">
                  <TranslatedText>AI Tools</TranslatedText>
                </h2>
              </div>
              <p className="text-xs text-muted-foreground ml-9">
                <TranslatedText>Build, write, and plan — powered by Aura AI</TranslatedText>
              </p>
            </div>

            {/* Credits balance + buy more */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 px-3 py-2 bg-card rounded-xl border border-border">
                <Sparkles className="w-3.5 h-3.5 text-primary" />
                <div className="leading-tight">
                  <p className="text-sm font-bold text-foreground">
                    {creditsLoading ? '...' : (aiCreditsBalance ?? 0)}
                  </p>
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider">
                    <TranslatedText>Credits left</TranslatedText>
                  </p>
                </div>
              </div>
              <Link
                to="/ai-tools?buy=1"
                className="flex items-center gap-1 px-3 py-2 rounded-xl bg-primary/10 hover:bg-primary/15 text-primary text-xs font-semibold transition-colors"
                aria-label="Buy more credits"
              >
                <Plus className="w-3.5 h-3.5" />
                <span className="hidden sm:inline"><TranslatedText>Top up</TranslatedText></span>
              </Link>
            </div>
          </div>

          {/* Featured: Aura AI Studio */}
          <Link
            to="/ai-tools?tool=ad-creative"
            className="group block mb-4 p-4 sm:p-5 rounded-2xl bg-gradient-to-br from-primary/10 via-card to-accent/10 border border-primary/20 hover:border-primary/40 hover:shadow-lg transition-all relative overflow-hidden"
          >
            <div className="absolute -top-8 -right-8 w-32 h-32 bg-primary/10 rounded-full blur-2xl opacity-60" />
            <div className="relative flex items-center gap-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary/20">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <h3 className="text-sm sm:text-base font-bold text-foreground truncate">
                    <TranslatedText>Aura AI Studio</TranslatedText>
                  </h3>
                  <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/15 text-primary">
                    <TranslatedText>All tools</TranslatedText>
                  </span>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-1">
                  <TranslatedText>Your full AI workspace to launch and grow your online business</TranslatedText>
                </p>
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all flex-shrink-0" />
            </div>
          </Link>

          {/* Category: Create */}
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-2 ml-1">
            <TranslatedText>Create</TranslatedText>
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5 mb-5">
            {[
              { id: 'ad-creative', icon: Megaphone, name: 'Ad Creative Generator', desc: 'Scroll-stopping visuals for ads', cost: 2, color: 'from-pink-500 to-rose-500' },
              { id: 'video-prompt', icon: Video, name: 'Video Prompt Builder', desc: 'Cinematic AI video prompts', cost: 2, color: 'from-purple-500 to-indigo-500' },
              { id: 'ad-copy', icon: PenTool, name: 'Ad Copy & Quotes', desc: 'Headlines, descriptions, CTAs', cost: 1, color: 'from-blue-500 to-cyan-500' },
            ].map((tool, i) => (
              <motion.div
                key={tool.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.55 + i * 0.05 }}
              >
                <Link
                  to={`/ai-tools?tool=${tool.id}`}
                  className="group relative flex items-center gap-3 p-3.5 pr-10 bg-card rounded-xl border border-border hover:border-primary/30 hover:shadow-md transition-all h-full"
                >
                  <span className="absolute top-2 right-2 flex items-center gap-0.5 text-[9px] font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                    <Sparkles className="w-2.5 h-2.5" />
                    {tool.cost}
                  </span>
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${tool.color} flex items-center justify-center flex-shrink-0 shadow-sm group-hover:scale-110 transition-transform`}>
                    <tool.icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-foreground leading-tight mb-0.5 break-words">
                      <TranslatedText>{tool.name}</TranslatedText>
                    </p>
                    <p className="text-[10px] text-muted-foreground line-clamp-2 leading-snug">
                      <TranslatedText>{tool.desc}</TranslatedText>
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Category: Strategize */}
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-2 ml-1">
            <TranslatedText>Strategize</TranslatedText>
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
            {[
              { id: 'marketing-plan', icon: Target, name: 'Marketing Plan', desc: 'Channels, budget, funnel, KPIs', cost: 3, color: 'from-emerald-500 to-teal-500' },
              { id: 'niche-strategy', icon: Compass, name: 'Strategy & Niche', desc: 'Positioning + competition analysis', cost: 3, color: 'from-amber-500 to-orange-500' },
              { id: 'launch-strategy', icon: Rocket, name: 'Launch 7/14/30', desc: 'Day-by-day launch plan', cost: 4, color: 'from-fuchsia-500 to-pink-500' },
              { id: 'website-builder', icon: Globe, name: 'Website / Store / App', desc: 'Build your online asset', cost: 0, color: 'from-slate-600 to-slate-800', free: true },
            ].map((tool, i) => (
              <motion.div
                key={tool.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.7 + i * 0.05 }}
              >
                <Link
                  to={`/ai-tools?tool=${tool.id}`}
                  className="group relative flex items-center gap-3 p-3.5 pr-12 bg-card rounded-xl border border-border hover:border-primary/30 hover:shadow-md transition-all h-full"
                >
                  {tool.free ? (
                    <span className="absolute top-2 right-2 text-[9px] font-bold text-emerald-600 bg-emerald-500/10 px-1.5 py-0.5 rounded uppercase tracking-wider">
                      Free
                    </span>
                  ) : (
                    <span className="absolute top-2 right-2 flex items-center gap-0.5 text-[9px] font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                      <Sparkles className="w-2.5 h-2.5" />
                      {tool.cost}
                    </span>
                  )}
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${tool.color} flex items-center justify-center flex-shrink-0 shadow-sm group-hover:scale-110 transition-transform`}>
                    <tool.icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-foreground leading-tight mb-0.5 break-words">
                      <TranslatedText>{tool.name}</TranslatedText>
                    </p>
                    <p className="text-[10px] text-muted-foreground line-clamp-2 leading-snug">
                      <TranslatedText>{tool.desc}</TranslatedText>
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </motion.div>


        {/* Freemium Conversion Teaser */}
        {clientInfo.clientType === 'freemium' && (
          <motion.div
            className="mb-12 p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-primary/10 via-card to-accent/10 border-2 border-primary/20 relative overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/2" />
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-bold text-foreground"><TranslatedText>Here's What You're Missing</TranslatedText></h3>
              </div>
              <p className="text-sm text-muted-foreground mb-6"><TranslatedText>You've seen Class 1 — but that's just the beginning. Here's a taste of what's waiting for you:</TranslatedText></p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                {[
                  { module: 'Module 2', title: 'Faceless Niche Architecture', teaser: 'Pick niches that print money without showing your face', icon: '🎯' },
                  { module: 'Module 5', title: 'AI Editing Mastery', teaser: 'Edit like a pro studio — 100% automated', icon: '🤖' },
                  { module: 'Module 8', title: 'Monetization Engine', teaser: 'Turn views into revenue on autopilot', icon: '💰' },
                  { module: 'Academy 2', title: 'Claude BOT Employee', teaser: 'Build AI employees that run your business — coming soon', icon: '⚡' },
                  { module: 'Academy 3', title: 'Digital Product Empire', teaser: 'Create & sell digital products with AI', icon: '📦' },
                  { module: 'Academy 4', title: 'Traffic & Growth Hacking', teaser: 'Scale to millions of views organically', icon: '📈' },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    className="p-4 bg-card/80 rounded-xl border border-border relative group hover:border-primary/30 hover:shadow-md transition-all"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.9 + i * 0.1 }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-card/90 rounded-xl z-10 flex items-end justify-center pb-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="text-[10px] font-semibold text-blue-600 uppercase tracking-wider flex items-center gap-1">
                        <Lock className="w-3 h-3" /> <TranslatedText>Upgrade to Unlock</TranslatedText>
                      </span>
                    </div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{item.icon} {item.module}</p>
                    <p className="text-sm font-semibold text-foreground mb-1">{item.title}</p>
                    <p className="text-xs text-muted-foreground">{item.teaser}</p>
                  </motion.div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-3">
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold h-12 px-8 rounded-xl shadow-lg shadow-blue-600/20"
                  disabled={isProcessingCheckout}
                  onClick={async () => {
                    setIsProcessingCheckout(true);
                    try { await createCheckoutSession('monthly'); } catch { toast.error('Failed to start checkout'); } finally { setIsProcessingCheckout(false); }
                  }}
                >
                  {isProcessingCheckout ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                  <TranslatedText>Unlock the Full System</TranslatedText>
                </Button>
                <p className="text-xs text-muted-foreground"><TranslatedText>Starting at</TranslatedText> ${monthlyPrice}/<TranslatedText>month</TranslatedText> · <TranslatedText>7-day guarantee</TranslatedText></p>
              </div>
            </div>
          </motion.div>
        )}

        {/* ═══ QUICK LINKS ═══ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mb-12"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Link
              to="/leaderboard"
              className="p-5 bg-muted/40 rounded-2xl text-center hover:bg-muted/70 transition-all group border border-transparent hover:border-border"
            >
              <Trophy className="w-5 h-5 text-primary mx-auto mb-2 group-hover:scale-110 transition-transform" />
              <p className="text-foreground/80 font-semibold text-xs"><TranslatedText>Leaderboard</TranslatedText></p>
            </Link>
            <a
              href="https://open.spotify.com/show/1VnoF1p9A8NpZlHGFv60B3?si=NwChlsmVQR2MOP-JsaMNNA"
              target="_blank"
              rel="noopener noreferrer"
              className="p-5 bg-muted/40 rounded-2xl text-center hover:bg-muted/70 transition-all group border border-transparent hover:border-border"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-green-500 mx-auto mb-2 group-hover:scale-110 transition-transform">
                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
              </svg>
              <p className="text-foreground/80 font-semibold text-xs"><TranslatedText>Podcast</TranslatedText></p>
            </a>
            <div className="p-5 bg-muted/40 rounded-2xl text-center hover:bg-muted/70 transition-all border border-transparent hover:border-border">
              <p className="text-foreground/80 font-semibold text-xs mb-2"><TranslatedText>Follow Us</TranslatedText></p>
              <div className="flex justify-center gap-3">
                <a href="https://www.instagram.com/facelessaura.club/" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center text-white hover:scale-110 transition-transform">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                  </svg>
                </a>
                <a href="https://www.tiktok.com/@facelessaura.club" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-foreground flex items-center justify-center text-background hover:scale-110 transition-transform">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.75a8.18 8.18 0 004.76 1.52V6.84a4.84 4.84 0 01-1-.15z"/>
                  </svg>
                </a>
              </div>
            </div>
            <a 
              href="mailto:hello@auradynamics.management" 
              className="p-5 bg-muted/40 rounded-2xl text-center hover:bg-muted/70 transition-all block border border-transparent hover:border-border"
            >
              <p className="text-foreground/80 font-semibold text-xs mb-1"><TranslatedText>Support</TranslatedText></p>
              <p className="text-[10px] text-muted-foreground">hello@auradynamics.management</p>
            </a>
          </div>
        </motion.div>
      </div>
      
      {/* Footer */}
      <Footer />
      
      <PlanSelectionDialog open={showPlanDialog} onOpenChange={setShowPlanDialog} />
    </div>
  );
};

export default Dashboard;